import { ensureNoUndefinedProperties } from '@/Shared/Helper/EnsureNoUndefinedProperties'
import MeetingData from '@/App/Booking/Meeting/Entity/MeetingData'
import Service from '@/App/Service/Entity/Service'
import User from '@/App/User/Entity/User'
import CustomFieldResponseData from '@/App/CustomField/Entity/CustomFieldResponseData'
import {
    BookingSchedulingStateEnum,
    type BookingDataRawDataApp,
} from '@/Shared/AutoGenerated/Main'
import Customer from '@/App/Customer/Entity/Customer'
import Booking from '@/App/Booking/Booking/Entity/Booking'
import MeetingRecurringDate from '@/App/Booking/Recurring/Entity/MeetingRecurringDate'
import type HasSchedulingStateInterface from '@/App/Booking/Shared/Entity/HasSchedulingStateInterface'
import ReservationData from '@/App/Booking/Reservation/Entity/ReservationData'
import type {
    SchedulingStateBadgeColor,
    SchedulingStateBadgeLabel,
} from '@/App/Booking/Shared/Entity/HasSchedulingStateInterface'

export default class BookingData implements HasSchedulingStateInterface {
    constructor(
        public readonly booking: Booking,
        public readonly meetingsData: MeetingData[],

        // Computed
        public readonly nextMeetingData: MeetingData | null,
        public readonly service: Service,
        public readonly host: User,
        public readonly customFieldResponsesData: CustomFieldResponseData[],
        public readonly customer: Customer,
        public readonly meetingRecurringDatesInAvailabilityTimezone: MeetingRecurringDate[],
        public readonly schedulingState: BookingSchedulingStateEnum,
        public readonly notes: string | null,
        public readonly cannotBeCanceled: boolean,
    ) {
        ensureNoUndefinedProperties(this)
    }

    isRecurring(): boolean {
        return this.booking.isRecurring()
    }

    getFirstMeetingData(): MeetingData {
        if (this.meetingsData.length === 0) {
            throw new Error(
                `Booking ${this.booking.id.value} does not have any meetings.`,
            )
        }

        return this.meetingsData[0]
    }

    getSingleExistingReservationData(): ReservationData {
        return this.getFirstMeetingData().getSingleExistingReservationData()
    }

    // ------------------------
    // HasSchedulingStateInterface implementation
    // ------------------------

    getSchedulingStateBadgeLabel(): SchedulingStateBadgeLabel {
        return this.schedulingState
    }

    getSchedulingStateBadgeColor(): SchedulingStateBadgeColor {
        if (this.schedulingState === BookingSchedulingStateEnum.Scheduled) {
            return 'success'
        }

        if (this.schedulingState === BookingSchedulingStateEnum.Canceled) {
            return 'secondary'
        }

        if (this.schedulingState === BookingSchedulingStateEnum.Completed) {
            return 'info'
        }

        throw new Error(`Unknown meeting state ${this.schedulingState}`)
    }

    // ------------------------
    // Hydration
    // ------------------------

    static hydrateSingle(entityData: BookingDataRawDataApp): BookingData {
        return new BookingData(
            Booking.hydrateSingle(entityData.bookingRawData),
            MeetingData.hydrateMultiple(entityData.meetingsDataRawData),

            // Computed
            MeetingData.hydrateSingleOrNull(entityData.nextMeetingDataRawData),
            Service.hydrateSingle(entityData.serviceRawData),
            User.hydrateSingle(entityData.hostRawData),
            CustomFieldResponseData.hydrateMultiple(
                entityData.customFieldResponsesDataRawData,
            ),
            Customer.hydrateSingle(entityData.customerRawData),
            MeetingRecurringDate.hydrateMultiple(
                entityData.meetingRecurringDatesInAvailabilityTimezoneRawData,
            ),
            entityData.schedulingState,
            entityData.notes,
            entityData.cannotBeCanceled,
        )
    }

    static hydrateMultiple(
        entitiesData: BookingDataRawDataApp[],
    ): BookingData[] {
        return entitiesData.map((entityData) => this.hydrateSingle(entityData))
    }
}
